import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useId } from "react";

import { brandPrimary, zinc } from "@/ui/tokens";

/** The border and fill every section of the Context tab shares, so the sections read as one set of cards. */
export const contextBlockSx = {
  bgcolor: zinc[900],
  border: `1px solid ${zinc[800]}`,
  borderRadius: 1.5,
} as const;

/**
 * The grid a section lays side-by-side fields out in, the Info section's and the route head's: as many columns as fit
 * at 220px or more, stacking once the panel is too narrow, split by hairlines where the grid's own fill shows through.
 */
export const contextFieldGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "1px",
  bgcolor: zinc[800],
  "& > *": { bgcolor: zinc[900] },
} as const;

/** The accent that marks what a section wants the eye on: the route's next stop, and a clock that is running. */
export const CONTEXT_ACCENT = "#60a5fa";

/**
 * The three styles a Context value comes in, so every section reads its numbers and words the same way.
 *
 * `hero` is the one number a section is read for: the PTA, the unit, a clock's time left. `value` is every other
 * value: a name, a status, a stop. `subtext` qualifies a value: its time, date, miles, references, or footnote.
 */
export type ContextTextVariant = "hero" | "value" | "subtext";

const CONTEXT_TEXT_SX: Record<ContextTextVariant, SxProps<Theme>> = {
  hero: { fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.2, color: "white", fontVariantNumeric: "tabular-nums" },
  value: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.35, color: "white" },
  subtext: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.4, color: zinc[400] },
};

/** A run of Context text in one of the tab's three value styles (see {@link ContextTextVariant}). */
export function ContextText({
  variant,
  component = "div",
  testId,
  sx,
  children,
}: {
  variant: ContextTextVariant;
  component?: "div" | "span";
  testId?: string;
  /** Layout and accent overrides; the style's size and weight stay the variant's. */
  sx?: SxProps<Theme>;
  children: React.ReactNode;
}) {
  return (
    <Typography
      component={component}
      data-sb={testId}
      sx={[CONTEXT_TEXT_SX[variant], { minWidth: 0, overflowWrap: "anywhere" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Typography>
  );
}

/** The small uppercase label a field, a clock, or a reference opens with. */
export function ContextCaption({
  component = "div",
  children,
}: {
  component?: "div" | "span";
  children: React.ReactNode;
}) {
  return (
    <Typography
      component={component}
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        lineHeight: 1.4,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: zinc[500],
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * One of the Context tab's four sections — Info, HOS, Route, Storyboard status — as a card opening on a header band.
 *
 * The band is the section's signature, so the eye finds it by scanning down the panel: its icon and its name in
 * capitals, in the brand's purple, on a tint of it, with an accent rule down its left edge and a band tall enough to
 * read as a break between sections rather than one more row. The name is the section's heading, so assistive
 * technology can move between sections the same way.
 */
export function ContextSection({
  icon,
  title,
  testId,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  testId: string;
  children: React.ReactNode;
}) {
  const headingId = useId();
  return (
    <Box
      component="section"
      data-sb={testId}
      aria-labelledby={headingId}
      sx={{ ...contextBlockSx, flex: "none", overflow: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 40,
          px: 1.5,
          bgcolor: `color-mix(in oklch, ${brandPrimary[500]} 18%, ${zinc[900]})`,
          borderBottom: `1px solid color-mix(in oklch, ${brandPrimary[500]} 40%, ${zinc[800]})`,
          boxShadow: `inset 3px 0 0 ${brandPrimary[400]}`,
        }}
      >
        <Box sx={{ display: "inline-flex", color: brandPrimary[300] }}>{icon}</Box>
        <Typography
          component="h3"
          id={headingId}
          sx={{
            m: 0,
            fontSize: "0.8125rem",
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: brandPrimary[200],
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

/**
 * One labelled field inside a section: an optional grey icon, the caption with an optional note beside it, then the
 * field's values stacked under it in the tab's three text styles.
 */
export function ContextField({
  icon,
  label,
  note,
  testId,
  children,
}: {
  icon?: React.ReactNode;
  /** The caption; a field with none shows its values alone. */
  label?: string;
  /** What the caption stands for, beside it: the PTA's "Planned Time of Availability". */
  note?: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <Box data-sb={testId} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, px: 1.5, py: 1.25, minWidth: 0 }}>
      {icon && <Box sx={{ color: zinc[500], mt: "1px", display: "inline-flex", flex: "none" }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {label && (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
            <ContextCaption>{label}</ContextCaption>
            {note && (
              <ContextText variant="subtext" component="span">
                {note}
              </ContextText>
            )}
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
}
